import { useEffect, useMemo, useState } from "react";
import { DashboardLayout } from "@/components/layout/DashboardLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { apiList } from "@/lib/api";
import { Award, Percent, Target } from "lucide-react";
import {
  calcularComissao,
  calcularMeta,
  competenciaAtual,
  monthInputValue,
  OrdemServicoPerformance,
  periodoDoMes,
  periodoPessoa,
  producaoClinicaCompleta,
  producaoDentistaCompleta,
} from "@/lib/performance";
import { useActiveClinicaId } from "@/hooks/useActiveClinicaId";
import { parseLocalDate, toLocalISODate } from "@/lib/date-utils";

type Pessoa = {
  id: string;
  nome: string;
  email: string;
  recebe_meta?: boolean;
  meta_periodo?: string;
  meta_inicio?: string;
  meta_fim?: string;
  meta_valor?: number;
  meta_bonus?: number;
  recebe_comissao?: boolean;
  percentual_comissao?: number;
  base_comissao?: "unidade" | "producao_pessoal";
  dentista_id_comissao?: string;
};

type Agendamento = {
  id: string;
  dentista_id: string;
  data_agendamento: string;
  status?: string;
  valor?: number;
};

const money = (value: number) => value.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
type ModoCalculo = "perfil" | "mes" | "periodo";
const flagAtiva = (value: unknown) => value === true || value === 1 || String(value).toLowerCase() === "true" || String(value) === "1";
const temMeta = (pessoa: Pessoa) => flagAtiva(pessoa.recebe_meta) || Number(pessoa.meta_valor || 0) > 0 || Number(pessoa.meta_bonus || 0) > 0;
const temComissao = (pessoa: Pessoa) => flagAtiva(pessoa.recebe_comissao) || Number(pessoa.percentual_comissao || 0) > 0;

export default function MetasComissoes() {
  const activeClinicaId = useActiveClinicaId();
  const [dentistas, setDentistas] = useState<Pessoa[]>([]);
  const [funcionarios, setFuncionarios] = useState<Pessoa[]>([]);
  const [agendamentos, setAgendamentos] = useState<Agendamento[]>([]);
  const [ordensServico, setOrdensServico] = useState<OrdemServicoPerformance[]>([]);
  const [loading, setLoading] = useState(true);
  const [modoCalculo, setModoCalculo] = useState<ModoCalculo>("perfil");
  const [mesCalculo, setMesCalculo] = useState(monthInputValue());
  const periodoMesAtual = periodoDoMes();
  const [periodoInicio, setPeriodoInicio] = useState(toLocalISODate(periodoMesAtual.inicio));
  const [periodoFim, setPeriodoFim] = useState(toLocalISODate(periodoMesAtual.fim));

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const [dentistasRows, funcionariosRows, agendamentosRows, ordensServicoRows] = await Promise.all([
          apiList<Pessoa>("dentistas"),
          apiList<Pessoa>("funcionarios"),
          apiList<Agendamento>("agendamentos"),
          apiList<OrdemServicoPerformance>("ordem_servico"),
        ]);
        setDentistas(dentistasRows);
        setFuncionarios(funcionariosRows);
        setAgendamentos(agendamentosRows);
        setOrdensServico(ordensServicoRows);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [activeClinicaId]);

  const rows = useMemo(() => {
    const competencia = competenciaAtual();
    const periodoManual = () => {
      const inicio = parseLocalDate(periodoInicio);
      const fim = parseLocalDate(periodoFim);
      inicio.setHours(0, 0, 0, 0);
      fim.setHours(23, 59, 59, 999);
      return { inicio, fim };
    };
    const periodoSelecionado = (pessoa: Pessoa) => {
      if (modoCalculo === "mes") return periodoDoMes(mesCalculo);
      if (modoCalculo === "periodo") return periodoManual();
      return temMeta(pessoa) ? periodoPessoa(pessoa) : competencia;
    };

    const dentistaRows = dentistas.map((dentista) => {
      const periodo = periodoSelecionado(dentista);
      const producao = producaoDentistaCompleta(agendamentos, ordensServico, dentista.id, periodo.inicio, periodo.fim);
      const meta = calcularMeta(dentista, producao);
      const recebeMeta = temMeta(dentista);
      const recebeComissao = temComissao(dentista);
      const percentual = recebeComissao ? Number(dentista.percentual_comissao || 0) : 0;
      return {
        id: `dentista-${dentista.id}`,
        nome: dentista.nome,
        tipo: "Dentista",
        producao,
        comissao: calcularComissao(producao, percentual),
        percentualComissao: percentual,
        meta: meta.valor,
        bonus: recebeMeta ? meta.bonusLiberado : 0,
        bonusPrevisto: meta.bonusPrevisto,
        percentualMeta: meta.percentual,
        falta: meta.falta,
        bateu: meta.bateu,
        recebeMeta,
        recebeComissao,
        periodo,
      };
    });

    const funcionarioRows = funcionarios.map((funcionario) => {
      const periodo = periodoSelecionado(funcionario);
      const producao = funcionario.base_comissao === "producao_pessoal" && funcionario.dentista_id_comissao
        ? producaoDentistaCompleta(agendamentos, ordensServico, funcionario.dentista_id_comissao, periodo.inicio, periodo.fim)
        : producaoClinicaCompleta(agendamentos, ordensServico, periodo.inicio, periodo.fim);
      const meta = calcularMeta(funcionario, producao);
      const recebeMeta = temMeta(funcionario);
      const recebeComissao = temComissao(funcionario);
      const percentual = recebeComissao ? Number(funcionario.percentual_comissao || 0) : 0;
      return {
        id: `funcionario-${funcionario.id}`,
        nome: funcionario.nome,
        tipo: "Funcionário",
        producao: recebeMeta || recebeComissao ? producao : 0,
        comissao: recebeComissao ? calcularComissao(producao, percentual) : 0,
        percentualComissao: percentual,
        meta: meta.valor,
        bonus: recebeMeta ? meta.bonusLiberado : 0,
        bonusPrevisto: meta.bonusPrevisto,
        percentualMeta: meta.percentual,
        falta: meta.falta,
        bateu: meta.bateu,
        recebeMeta,
        recebeComissao,
        baseComissao: funcionario.base_comissao === "producao_pessoal" ? "Produção própria" : "Produção da unidade",
        periodo,
      };
    });

    return [...dentistaRows, ...funcionarioRows].filter((row) => row.recebeMeta || row.recebeComissao);
  }, [agendamentos, ordensServico, dentistas, funcionarios, mesCalculo, modoCalculo, periodoFim, periodoInicio]);

  const alterarMesCalculo = (mes: string) => {
    setMesCalculo(mes);
    const periodo = periodoDoMes(mes);
    setPeriodoInicio(toLocalISODate(periodo.inicio));
    setPeriodoFim(toLocalISODate(periodo.fim));
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold">Metas e Comissões</h1>
          <p className="text-muted-foreground">Acompanhe produção, comissão de dentistas e bônus de meta.</p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Período de cálculo</CardTitle>
            <CardDescription>Escolha se deseja calcular pelo perfil cadastrado, por mês inteiro ou por um prazo específico, incluindo períodos antigos.</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4 md:grid-cols-3">
              <div className="space-y-2">
                <Label>Como deseja calcular?</Label>
                <Select value={modoCalculo} onValueChange={(value: ModoCalculo) => setModoCalculo(value)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="perfil">Período cadastrado no perfil</SelectItem>
                    <SelectItem value="mes">Mês inteiro</SelectItem>
                    <SelectItem value="periodo">Período personalizado</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {modoCalculo === "mes" && (
                <div className="space-y-2">
                  <Label>Mês de cálculo</Label>
                  <Input type="month" value={mesCalculo} onChange={(event) => alterarMesCalculo(event.target.value)} />
                </div>
              )}
              {modoCalculo === "periodo" && (
                <>
                  <div className="space-y-2">
                    <Label>Início</Label>
                    <Input type="date" value={periodoInicio} onChange={(event) => setPeriodoInicio(event.target.value)} />
                  </div>
                  <div className="space-y-2">
                    <Label>Fim</Label>
                    <Input type="date" value={periodoFim} onChange={(event) => setPeriodoFim(event.target.value)} />
                  </div>
                </>
              )}
            </div>
          </CardContent>
        </Card>

        <div className="grid gap-4 md:grid-cols-3">
          <Card>
            <CardContent className="p-6">
              <Percent className="mb-2 h-5 w-5 text-primary" />
              <p className="text-sm text-muted-foreground">Dentistas com comissão</p>
              <p className="text-2xl font-bold">{dentistas.filter((dentista) => dentista.recebe_comissao).length}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-6">
              <Target className="mb-2 h-5 w-5 text-primary" />
              <p className="text-sm text-muted-foreground">Pessoas com meta</p>
              <p className="text-2xl font-bold">{rows.filter((row) => row.recebeMeta).length}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-6">
              <Award className="mb-2 h-5 w-5 text-primary" />
              <p className="text-sm text-muted-foreground">Bônus previstos</p>
              <p className="text-2xl font-bold">{money(rows.reduce((sum, row) => sum + row.bonus, 0))}</p>
            </CardContent>
          </Card>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Fechamento calculado</CardTitle>
            <CardDescription>Produção realizada no período, comissão calculada pelo percentual cadastrado e bônus liberado somente quando a meta é atingida.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {loading ? (
              <p className="text-sm text-muted-foreground">Carregando...</p>
            ) : rows.length === 0 ? (
              <div className="rounded-md border border-dashed p-4 text-sm text-muted-foreground">
                Nenhuma meta ou comissão configurada nesta unidade. Cadastre meta no perfil do funcionário ou dentista, ou comissão no perfil do dentista/funcionário, para o cálculo aparecer aqui.
              </div>
            ) : (
              rows.map((row) => (
                <div key={row.id} className="rounded-lg border p-4">
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                      <h3 className="font-semibold">{row.nome}</h3>
                      <p className="text-sm text-muted-foreground">
                        {row.tipo} | {toLocalISODate(row.periodo.inicio)} a {toLocalISODate(row.periodo.fim)}
                      </p>
                    </div>
                    <div className="text-sm sm:text-right">
                      <p>Produção: <strong>{money(row.producao)}</strong></p>
                      <p>Comissão: <strong>{money(row.comissao)}</strong> {row.percentualComissao > 0 && <span className="text-muted-foreground">({row.percentualComissao}%)</span>}</p>
                      {row.tipo === "Funcionário" && row.recebeComissao && <p className="text-xs text-muted-foreground">Base: {row.baseComissao}</p>}
                      <p>Bônus meta: <strong>{money(row.bonus)}</strong> <span className="text-muted-foreground">/ previsto {money(row.bonusPrevisto)}</span></p>
                    </div>
                  </div>
                  {row.recebeMeta && (
                    <div className="mt-3 space-y-2">
                      <Progress value={row.percentualMeta} />
                      <div className="flex justify-between text-xs text-muted-foreground">
                        <span>{row.percentualMeta.toFixed(1)}% da meta {row.bateu ? "atingida" : `(faltam ${money(row.falta)})`}</span>
                        <span>{money(row.producao)} / {money(row.meta)}</span>
                      </div>
                    </div>
                  )}
                </div>
              ))
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
